import React from 'react';
import { registerFormat } from '../registry';
import { zplCapabilities } from './capabilities';
import type { FormatDefinition, FormatRenderOptions, FormatRenderResult } from '../types';
import type { Template, TextElement } from '../../types';
import { buildConditionTypeByKey } from '../../conditions';

const zplFormat: FormatDefinition = {
  id: 'zpl',
  displayName: 'ZPL',
  fileExtension: 'zpl',
  mimeType: 'application/octet-stream',
  defaultDpi: 203,
  dpiOptions: [
    { label: 'Standard (203 DPI)', value: 203, recommended: true },
    { label: 'High-res (300 DPI)', value: 300 },
  ],
  capabilities: zplCapabilities,
  recommended: false,

  PreviewPanel: React.lazy(() => import('./ZplPreviewPanel')),

  templateDefaults: {
    background: '#ffffff',
  },

  async renderLabels(options: FormatRenderOptions): Promise<FormatRenderResult> {
    const { generateLabelZpl } = await import('./renderer');
    const { prepareRenderData } = await import('../../templateRuntime');
    const {
      createFormatTextContent,
      createResolveImageUrl,
      createResolveBarcodeData,
      fetchImageBytes,
    } = await import('../../services/pdfGenerator');

    const {
      template,
      labelCount,
      qrIndexOffset = 0,
      data,
      variableExamples = {},
      topOffset = 0,
      leftOffset = 0,
      layout,
    } = options;
    const dotsPerUnit = layout.unit === 'cm' ? (template.dpi ?? 203) / 2.54 : (template.dpi ?? 203);

    const { fullData, themeData, rawDataContext } = prepareRenderData({
      template,
      coa: data.coa,
      job: data.job,
      product: data.product,
      common: data.common,
    });

    const conditionTypeByKey = buildConditionTypeByKey(template.themeVariables);

    const resolverCtx = {
      data: fullData,
      rawDataContext,
      variableExamples,
      conditionTypeByKey,
      themeVariables: template.themeVariables,
      legacyImages: options.legacyImages,
      legacyFonts: options.legacyFonts,
      newAssetImages: options.newAssetImages,
      newAssetFonts: options.newAssetFonts,
      defaultFont: options.defaultFont,
      // Match generateLabelPdf: unresolved tokens must not print on final labels.
      isPreview: options.isPreview ?? false,
      hasCoa: options.hasCoa,
    };

    const formatTextContent = createFormatTextContent(resolverCtx);
    const resolveImageUrl = createResolveImageUrl(resolverCtx);
    const resolveBarcodeData = createResolveBarcodeData(resolverCtx);

    const qrValues = data.qrValues ?? [];

    const result = await generateLabelZpl({
      template,
      labelCount,
      topOffset: Math.round(topOffset * dotsPerUnit),
      leftOffset: Math.round(leftOffset * dotsPerUnit),
      qrValues,
      qrIndexOffset,
      commonData: fullData,
      themeData,
      formatTextContent: formatTextContent as (el: TextElement, d?: Record<string, unknown>) => string,
      resolveImageUrl: resolveImageUrl as (el: any, d?: Record<string, unknown>) => string | undefined,
      resolveBarcodeData: resolveBarcodeData as (el: any, d?: Record<string, unknown>) => string,
      fetchImageBytes,
    });

    return {
      format: 'zpl',
      blob: result.blob,
      url: result.url,
      labelCount: result.labelCount,
      extra: {
        zplString: result.zplString,
      },
    };
  },

  revokeUrl(url: string) {
    URL.revokeObjectURL(url);
  },
};

registerFormat(zplFormat);

export default zplFormat;
